/* @flow */
import { getterFromKeyPath } from 'trustar/utils';
import { createSelector } from 'reselect';
import { OrderedMap, Map, fromJS } from 'immutable';
import { StandardMarketplace } from './types.js';
import * as DataFeeds from 'modules/data-feeds';
import * as Subscriptions from 'modules/subscriptions';
import { feedsMap } from 'modules/data-feeds/getters.js';

export const displayOptions = getterFromKeyPath(['marketplace', 'options']);
export const allFeedsMap = getterFromKeyPath(['dataFeeds', 'feeds']);
export const allSubsMap = getterFromKeyPath(['subscriptions', 'subscriptions']);
export const allInts = getterFromKeyPath(['marketplace', 'fixedIntegrationsList']);
export const loading = getterFromKeyPath(['marketplace', 'loading']);


/** Utility function for converting a DataFeed to StandardMarketplace Record  */
export const feedToStandard = (feed: DataFeeds.types.DataFeed) => {
  if (feed) {
    return StandardMarketplace({
      feedGuid : feed.guid,
      name : feed.name,
      iconUrl : feed.iconUrl,
      description : feed.description,
      credentialsType : feed.credentialsType,
      externalUrl : feed.externalUrl,
      label : feed.label,
      created: feed.created,
      updated: feed.updated,
    });
  }
};


/** Utility function for converting a Subscription to StandardMarketplace Record  */
export const subscriptionToStandard = (subscription: Subscriptions.types.Subscription, feed: DataFeeds.types.DataFeed) => {
  if (subscription) {
    return StandardMarketplace({
      name : feed.get('name'),
      feedGuid : subscription.get('feedGuid'),
      enclaveGuid : subscription.get('enclaveGuid'),
      companyGuid : subscription.get('companyGuid'),
      state : subscription.get('state'),
      lastPulled : subscription.get('lastPulled'),
      subscriptionDate: subscription.get('created'),
    });
  }
};


/** Utility function for converting an Integration to StandardMarketplace Record  */
export const integrationToStandard = (integration: Object) => {
  if (integration) {
    return StandardMarketplace({
      name : integration.name,
      iconUrl : integration.iconUrl,
      description : integration.description,
      externalUrl : integration.externalUrl,
      label : integration.label,
    });
  }
};


/** Grabs all feeds and collects them into a List of StandardMarketplace Records */
export const allFeeds = createSelector(
  [ allFeedsMap, displayOptions ],
  (feeds: OrderedMap<any, any>, options: Map<any, any>) => {
    let result = feeds;
    if (feeds && !feeds.isEmpty()) {
      result = feeds.map(feed => feedToStandard(feed));
      return result.toList();
    }
  }
);

/** Grabs all subscriptions and collects them into a list of StandardMarketplace Records */
export const allSubs = createSelector(
  [ allSubsMap, feedsMap, displayOptions ],
  (subs: OrderedMap<any, any>, feedsMap: OrderedMap<String, DataFeeds.types.DataFeed>, options: Map<any, any>) => {
    let result = subs;
    if (subs && !subs.isEmpty()) {
      result = subs.map(sub => subscriptionToStandard(sub, feedsMap.get(sub.get('feedGuid'))));
      return result.toList();
    }
  },
);

/** Aggregates all DataFeeds and Subscriptions into an OrderedMap of StandardMarketplace Records  */
export const allFeedsAndSubs = createSelector(
  [ allFeedsMap, allSubsMap ],
  (feeds: OrderedMap<String, DataFeeds.types.DataFeed>, subs: OrderedMap<String, Subscriptions.types.Subscription>) => {
    let result;
    if (feeds) {
      // go thru the feeds and see if by the feedGuid, there is a corresponding subscription with that feedGuid
      result = feeds.map(feed => {
        const sub = subs.get(feed.get('guid'));
        // if there is a corresponding subscription: grab its relevant properties, and use them along with
        // the feed properties to create the StandardMarketplace Record
        return StandardMarketplace({
          feedGuid: feed.guid,
          name: feed.name,
          iconUrl: feed.iconUrl,
          description: feed.description,
          credentialsType: feed.credentialsType,
          maximumAccess: feed.maximumAccess,
          externalUrl: feed.externalUrl,
          label: feed.label,
          enclaveGuid: sub? sub.get('enclaveGuid') : null,
          companyGuid: sub? sub.get('companyGuid') : null,
          state: sub? sub.get('state') : null,
          lastPulled: sub? sub.get('lastPulled') : null,
          subscriptionDate: sub? sub.get('created') : null,
          created: feed.created,
          updated: feed.updated,
        });
      });
    }
    return result;
  }
);

const listToOrderedMap = (list) => {
  return fromJS(list);
};

/** Fixed list of all the available integrations on the platform (as of Oct. 2019) - This should
 * be a dynamic list managed by trustar power users. */
export const fixedIntegrationsList = [
  {
    name: 'Splunk SOAR',
    description: 'Orchestrate security workflows and automate tasks in seconds to empower your SOC, work smarter and respond faster.',
    iconUrl: 'https://s3.amazonaws.com/trustar-production-staticcontent/images/splunk-soar-logo.png',
    externalUrl: 'https://www.splunk.com/en_us/products/splunk-security-orchestration-and-automation.html',
  },
  {
    name: 'Splunk ES',
    description: 'The Splunk ES workflow app facilitates the detection, investigation, and response of notable events and alerts.',
    iconUrl: 'https://trustar-staging-staticcontent.s3.amazonaws.com/logos/60bc78da-9f6a-4ca8-ae9c-b9d95210b645.png',
    externalUrl: 'https://support.trustar.co/category/cnz0mrvzh1-splunk-unified',
  },
  {
    name: 'ServiceNow',
    description: 'This plugin allows users to utilize context of TruSTAR’s IOCs and incidents within ServiceNow workflow.',
    iconUrl: 'https://s3.amazonaws.com/trustar-production-staticcontent/images/servicenow-logo.png',
    externalUrl: 'https://support.trustar.co/article/5qzlkkzw7g-install-tru-star-for-service-now-v-2',
  },
  {
    name: 'Chrome Extension',
    description: 'This App allows users to highlight text from their Chrome browser and see what kind of context is available from the TruSTAR platform. If there is additional context available on TruSTAR a deep link is shown on the extension window that takes you to the TruSTAR Station.',
    iconUrl: 'https://s3.amazonaws.com/trustar-production-staticcontent/images/chrome-logo.png',
    externalUrl: 'https://support.trustar.co/article/r0J3AaBiHG-chrome-extension',
  },
].map(x => ({
  ...x,
  label: 'INTEGRATION',
}))
;

// For integrations AKA applications that are premium content
export const premiumIntegrations = listToOrderedMap(
  [
    {
      name: 'AWS GuardDuty',
      description: 'TruSTARs AWS GuardDuty Integration allows AWS users who have access to GuardDuty send GD”Findings” into their private enclaves in TruSTAR. The way the integration works is by having users send their Guard Duty Findings as events into TruSTAR. Users can create a Lambda function by importing TruSTAR’s custom script that sends findings as events in TruSTAR.',
      iconUrl: 'https://trustar-staging-staticcontent.s3.amazonaws.com/logos/3148948f-a37a-409b-a25f-eae807c3d7da.png',
      externalUrl: 'https://support.trustar.co/article/41mmladxan-guard-duty',
    },
  ].map(x => ({
    ...x,
    label: 'INTEGRATION',
    isPremiumContent: true,  // This may become our new way of determining paid content instead of closed source
  }))
);

/*
  These are premium intel sources which require API credentials to activate but ARE available to NON-PAYING companies
CISCP yes
A-ISAC
F-ISAC
FS-ISAC
H-ISAC TLP Amber Alerts
 */
export const openPremiumIntelSources = OrderedMap({
  CISCP: true,
  'A-ISAC': true,
  'F-ISAC': true,
  'FS-ISAC': true,
  'H-ISAC TLP Amber Alerts': true,
  'Taxii Client A': true,
  'Taxii Client B': true,
  'Taxii Client C': true,
});

export const allIntegrations = listToOrderedMap(fixedIntegrationsList);



// WEBPACK FOOTER //
// ./src/app/modules/marketplace/getters.js